import { generatePath } from 'react-router';

export enum SearchCategoryEnum {
    SgMarkets = 'sgmarkets',
    Societe = 'societe',
    Person = 'person',
    Company = 'company',
    All = ''
}

export enum MyAccountPageEnum {
    Settings = 'settings',
    Security = 'security',
    Signature = 'signature',
    PersonalInformation = 'personal-information'
}

export enum MyServicesPageEnum {
    Essentials = 'essentials',
    AllServices = 'all-services'
}

export enum AllServicesPageEnum {
    AllMyServices = 'all-my-services',
    AllMySgmServices = 'all-my-sgm-services',
    AllSgBookmarkedServices = 'all-sg-bookmarked-services'
}

export enum CatalogPageEnum {
    Browse = 'browse',
    ForYou = 'for-you'
}

export const RoutePaths = {
    Errors: {
        NotFound: {
            route: '*',
            url: () => '/error/not-found',
        },
        NotAuthorized: {
            route: '/error/not-authorized',
            url: () => RoutePaths.Errors.NotAuthorized.route,
        },
    },
    Home: {
        route: '/',
        url: () => '/',
    },
    MyServices: {
        route: '/myservices',
        url: () => RoutePaths.MyServices.route,
        Essentials: {
            route: '/myservices/essentials/:servicePath?/:serviceKey?/:language?',
            url: () => '/myservices/essentials',
        },
        AllServices: {
            route: '/myservices/all-services/:page?/:servicePath?/:serviceKey?',
            url: (page?: AllServicesPageEnum) => generatePath(RoutePaths.MyServices.AllServices.route, {
                page: page || null,
                servicePath: null,
                serviceKey: null,
            }),
        },
    },
    Catalog: {
        route: '/catalog',
        url: () => RoutePaths.Catalog.route,
        Browse: {
            route: '/catalog/browse/:category?/:subCategory?/:servicePath?/:serviceKey?',
            url: (category?: string, subCategory?: string) => generatePath(RoutePaths.Catalog.Browse.route, {
                category: category || null,
                subCategory: subCategory || null,
                servicePath: null,
                serviceKey: null,
            }),
        },
        ForYou: {
            route: '/catalog/for-you/:servicePath?/:serviceKey?',
            url: () => generatePath(RoutePaths.Catalog.ForYou.route, {
                servicePath: null,
                serviceKey: null,
            }),
        },
    },
    Search: {
        route: '/search/:searchTerm?/:searchCategory?/:servicePath?/:serviceKey?',
        url: (searchTerm?: string, searchCategory?: string) => generatePath(RoutePaths.Search.route, {
            searchTerm: searchTerm || null,
            searchCategory: searchCategory || null,
            servicePath: null,
            serviceKey: null,
        }),
    },
    Disclaimer: {
        title: 'Disclaimer',
        route: '/disclaimer',
        ByApplication: {
            route: '/disclaimer/page/:application',
            url: (application: string) => generatePath(RoutePaths.Disclaimer.ByApplication.route,
                { application: application }),
        },
        Validated: {
            route: '/disclaimer/file',
            url: () => RoutePaths.Disclaimer.Validated.route,
        },
        CountryRestricted: {
            route: '/disclaimer/countryrestricted',
            url: () => RoutePaths.Disclaimer.CountryRestricted.route,
        },
    },
    MyAccount: {
        title: 'My Account',
        route: '/myaccount/:page?',
        url: (page?: MyAccountPageEnum) => generatePath(RoutePaths.MyAccount.route, { page: page || MyAccountPageEnum.Settings }),
    },
    MyNotification: {
        title: 'My Notifications',
        route: '/mynotifications/:producer?',
        url: (producer?: string) => generatePath(RoutePaths.MyNotification.route, { producer: producer || null }),
    },
    Notification: {
        title: 'Notification',
        route: '/notification/:id/tracking/click',
        url: (id: string) => generatePath(RoutePaths.Notification.route, { id }),
    },
    // Old routes
    Services: {
        route: '/services/*',
    },
} as const;

export type RouteParams = {
    Errors: {
        NotFound: ExtractRouteParams<typeof RoutePaths.Errors.NotFound.route, string>,
        NotAuthorized: ExtractRouteParams<typeof RoutePaths.Errors.NotAuthorized.route, string>,
    },
    Home: ExtractRouteParams<typeof RoutePaths.Home.route, string>,
    MyServices: {
        Base: ExtractRouteParams<typeof RoutePaths.MyServices.route, string>,
        Essentials: ExtractRouteParams<typeof RoutePaths.MyServices.Essentials.route, string>,
        AllServices: ExtractRouteParams<typeof RoutePaths.MyServices.AllServices.route, string>,
    },
    Catalog: {
        Base: ExtractRouteParams<typeof RoutePaths.Catalog.route, string>,
        Browse: ExtractRouteParams<typeof RoutePaths.Catalog.Browse.route, string>,
        ForYou: ExtractRouteParams<typeof RoutePaths.Catalog.ForYou.route, string>,
    },
    Search: ExtractRouteParams<typeof RoutePaths.Search.route, string>,
    Disclaimer: {
        Base: ExtractRouteParams<typeof RoutePaths.Disclaimer.route, string>,
        ByApplication: ExtractRouteParams<typeof RoutePaths.Disclaimer.ByApplication.route, string>,
        Validated: ExtractRouteParams<typeof RoutePaths.Disclaimer.Validated.route, string>,
        CountryRestricted: ExtractRouteParams<typeof RoutePaths.Disclaimer.CountryRestricted.route, string>,
    },
    MyAccount: ExtractRouteParams<typeof RoutePaths.MyAccount.route, string>,
    MyNotification: ExtractRouteParams<typeof RoutePaths.MyNotification.route, string>,
    Notification: ExtractRouteParams<typeof RoutePaths.Notification.route, string>,
    // Old routes
    Services: ExtractRouteParams<typeof RoutePaths.Services.route, string>,
};

type ExtractRouteOptionalParam<T extends string, U = string | number | boolean> = T extends `${infer Param}?`
    ? { [_ in Param]?: U }
    : T extends `${infer Param}*`
    ? { [_ in Param]?: U }
    : T extends `${infer Param}+`
    ? { [_ in Param]: U }
    : { [_ in T]: U };

/* eslint-disable */
type ExtractRouteParams<T extends string, U = string | number | boolean> = string extends T
    ? { [_ in string]?: U }
    : T extends `${infer _}:${infer ParamWithOptionalRegExp}/${infer Rest}`
    ? ParamWithOptionalRegExp extends `${infer Param}(${infer _})`
    ? ExtractRouteOptionalParam<Param, U> & ExtractRouteParams<Rest, U>
    : ExtractRouteOptionalParam<ParamWithOptionalRegExp, U> & ExtractRouteParams<Rest, U>
    : T extends `${infer _}:${infer ParamWithOptionalRegExp}`
    ? ParamWithOptionalRegExp extends `${infer Param}(${infer _})`
    ? ExtractRouteOptionalParam<Param, U>
    : ExtractRouteOptionalParam<ParamWithOptionalRegExp, U>
    : unknown;

const getQueryParameters = (parameters: Record<string, string | number | boolean | Date | undefined | string[] | number[]>) => {
    const search = new URLSearchParams();

    for (const parametersKey in parameters) {
        const value = parameters[parametersKey];
        if (value) {
            if (Array.isArray(value)) {
                value.forEach(v => search.append(parametersKey, getQueryParameterValue(v)));
            } else {
                search.append(parametersKey, getQueryParameterValue(value));
            }
        }
    }

    const str = search.toString();
    return str ? `?${str}` : '';
};

const getQueryParameterValue = (value: string | number | boolean | Date | undefined): string => {
    if (value instanceof Date) {
        return value?.toISOString() || '';
    }
    return value?.toString() || '';
};
